import React from 'react';
import cn from 'classnames';
import { BlueTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { DisparityBarChartStackProps } from 'libs/end-to-end/src/disparity';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const DisparityBarChartStack = ({
  debiasedDisparity,
  rawDisparity,
  controlledDisparity,
  variant,
  calibrationFraming,
  hasRunBiasTasks,
}: DisparityBarChartStackProps) => {
  const language = useLanguage();
  const isPerfectScore =
    rawDisparity.toFixed(0) === '0' &&
    controlledDisparity.toFixed(0) === '0' &&
    (debiasedDisparity === null || debiasedDisparity.toFixed(0) === '0');

  return (
    <Box
      display={'flex'}
      flexDirection={'col'}
      position={'relative'}
      w={'full'}
      gap={'xs'}
    >
      <DisparityBarChartStackRow
        variant={variant}
        color={'gray'}
        percentage={rawDisparity}
        tooltipTranslatedContent={language.rawDisparity()}
        isPerfectScore={isPerfectScore}
      />
      <DisparityBarChartStackRow
        variant={variant}
        color={'purple'}
        percentage={controlledDisparity}
        tooltipTranslatedContent={language.controlledDisparity()}
        isPerfectScore={isPerfectScore}
      />
      {hasRunBiasTasks && (
        <DisparityBarChartStackRow
          variant={variant}
          color={'blue'}
          percentage={debiasedDisparity ?? controlledDisparity}
          tooltipTranslatedContent={
            calibrationFraming === 'optimization'
              ? language.expectedDisparity()
              : language.debiasedDisparity()
          }
          isPerfectScore={isPerfectScore}
        />
      )}

      <Box
        position={'absolute'}
        left={'1/2'}
        ml={'ml-[-1px]'}
        w={'w-[2px]'}
        h={'full'}
        bg={isPerfectScore ? 'green-300' : 'gray-300'}
        pointerEvents={'none'}
      />
    </Box>
  );
};

const DisparityBarChartStackRow = ({
  color,
  variant,
  percentage,
  tooltipTranslatedContent,
  isPerfectScore,
}: {
  variant: 'default' | 'large';
  percentage: number;
  color: 'gray' | 'purple' | 'blue';
  tooltipTranslatedContent: TranslatedContent;
  isPerfectScore: boolean;
}) => {
  const language = useLanguage();
  const clampedPercentage = Math.min(Math.max(percentage, -100), 100);
  const side = clampedPercentage <= 0 ? 'left' : 'right';
  const widthPercentage = Math.abs(clampedPercentage);
  const backgroundStyle: Record<'gray' | 'purple' | 'blue', string> = {
    gray: 'radial-gradient(689.08% 141.42% at 0% 100%, #293B4D 0%, #5F758A 100%)',
    purple:
      'radial-gradient(359.1% 141.42% at 100% 0%, #7048E7 0%, #3F2881 100%)',
    blue: 'radial-gradient(467.95% 141.42% at 100% 0%, #0D99FF 0%, #076DD3 33%, #0028F9 100%)',
  };

  return (
    <BlueTooltip
      followCursor={true}
      title={
        <Spacing direction={'vertical'} size={'none'}>
          <S2Typography
            variant={'headline-5-alt'}
            textColorVariant={'white'}
            translatedContent={language.percentage({ value: percentage })}
          />
          <S2Typography
            variant={'body-7'}
            textColorVariant={'white'}
            translatedContent={tooltipTranslatedContent}
          />
        </Spacing>
      }
    >
      <div
        className={cn('w-full bg-gray-400 rounded relative', {
          'h-[36px]': variant === 'default',
          'h-[61px]': variant === 'large',
        })}
        style={{
          background: isPerfectScore
            ? '#F2FAF0'
            : 'radial-gradient(689.08% 141.42% at 0% 100%, rgba(41, 59, 77, 0.05) 0%, rgba(95, 117, 138, 0.05) 100%)',
        }}
      >
        <Box
          display={'flex'}
          position={'absolute'}
          h={'full'}
          top={'0'}
          w={'1/2'}
          left={side === 'right' ? '1/2' : undefined}
          right={side === 'left' ? '1/2' : undefined}
          pr={side === 'left' ? 'pr-[1px]' : undefined}
          pl={side === 'right' ? 'pl-[1px]' : undefined}
          justifyContent={side === 'left' ? 'end' : 'start'}
        >
          <div
            className={cn('h-full', {
              'rounded-l': side === 'left',
              'rounded-r': side === 'right',
            })}
            style={{
              background: backgroundStyle[color],
              width: `${widthPercentage}%`,
            }}
          />
        </Box>
      </div>
    </BlueTooltip>
  );
};
